import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import qs from "qs";
import moment from "moment";
import api from "../../services/api";
import { useMenuAuthInfo } from "../../services/GlobalUtil";
import { saveBlobAsFile } from "../../services/FileDownloadUtil";
import {
  POPUP_TARGET,
  toLocalInputDateTime,
  toUtcDateTime,
} from "../../services/NoticePopupUtil";
import BreadCrumbDiv from "../../components/common/BreadCrumbDiv";
import LoadingBoxDiv from "../../components/common/LoadingBoxDiv";
import InputWrapDiv from "../../components/common/InputWrapDiv";
import TopBoxDiv from "../../components/common/TopBoxDiv";
import ContentInput from "../../components/common/ContentInput";
import ContentTextarea from "../../components/common/ContentTextarea";
import FunctionButton from "../../components/common/FunctionButton";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import MessageDialog from "../../components/common/MessageDialog";
import TooltipWrapper from "../../components/common/TooltipWrapper";

const FILE_SLOTS = [0, 1, 2];
const DATETIME_FORMAT = "YYYY-MM-DDTHH:mm";

/**
 * 전역 CSS(index.scss)가 native 체크박스·라디오를 숨기고 인접 label을 이미지로 대체하므로,
 * 문구를 함께 노출하는 label은 이미지 폭만큼 왼쪽 여백을 둔다.
 */
const CHECK_LABEL_STYLE = {
  width: "auto",
  paddingLeft: "22px",
  lineHeight: "17px",
  fontSize: "13px",
};

/** 서버 결과 코드(ApiEnum) → 노출 메시지 코드 */
const RESULT_MESSAGE_CODES = {
  1300: "L0940", // 첨부파일명 중복
  1301: "L0764", // 허용되지 않는 확장자
  1302: "L1138", // 기간이 겹치는 팝업 공지 3건 초과
  1303: "L1139", // 팝업 노출 화면·기간 오류
};

/** 다국어 코드가 아직 등록되지 않은 환경에서도 문구가 보이도록 기본값을 둔다. */
const DIALOG_DEFAULT_MESSAGES = {
  L1138: "Up to 3 popup notices can be active at the same time.",
  L1139: "The end date must be later than the start date.",
  L1141: "Past dates cannot be selected.",
  L1142: "Failed to process the request.",
};


const MainNoticeViewPage = () => {
  const { id } = useParams();
  const isViewMode = Boolean(id);
  const { t } = useTranslation();
  const menuAuthInfo = useMenuAuthInfo();
  const [data, setData] = useState();
  const navigate = useNavigate();
  // 등록 화면은 처음부터 입력 가능하고, 상세 화면은 수정 버튼을 눌렀을 때 입력 가능해진다.
  const [isEditing, setIsEditing] = useState(!isViewMode);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [popupYn, setPopupYn] = useState(false);
  const [popupTarget, setPopupTarget] = useState(POPUP_TARGET.LOGIN);
  const [popupStrDtm, setPopupStrDtm] = useState("");
  const [popupEndDtm, setPopupEndDtm] = useState("");
  // 이미 시작된 팝업의 시작일시는 기존값 유지를 허용하고, 변경한 경우에만 과거 일자를 검증한다.
  const [originStrDtm, setOriginStrDtm] = useState("");
  const [files, setFiles] = useState([null, null, null]);
  const [deleteFiles, setDeleteFiles] = useState([false, false, false]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [errors, setErrors] = useState({
    title: false,
    content: false,
    popupPeriod: false,
  });

  const todayStart = moment().startOf("day").format(DATETIME_FORMAT);
  /*
    이미 시작된 팝업은 기존 시작일시를 그대로 유지할 수 있어야 한다.
    그런 경우 min 을 걸면 입력값이 min 보다 이전이 되어 유효하지 않은 값으로 표시되므로 제한을 두지 않는다.
    (변경했을 때의 당일 이후 검증은 validate 에서 수행한다)
  */
  const strDtmMin = originStrDtm && originStrDtm < todayStart ? undefined : todayStart;

  useEffect(() => {
    if (isViewMode && menuAuthInfo.view) {
      loadContentData();
    }
  }, []);

  const loadContentData = () => {
    api
      .post("/notice/main/detail", {
        id: id,
      })
      .then((response) => {
        const notice = response.data;
        setData(notice);
        setTitle(notice?.title || "");
        setContent(notice?.content || "");
        setPopupYn(notice?.popupYn === "Y");
        setPopupTarget(notice?.popupTarget || POPUP_TARGET.LOGIN);
        // 서버 저장값은 UTC이므로 로컬 시각으로 바꿔 입력란에 넣는다.
        setPopupStrDtm(toLocalInputDateTime(notice?.popupStrDtm));
        setPopupEndDtm(toLocalInputDateTime(notice?.popupEndDtm));
        setOriginStrDtm(toLocalInputDateTime(notice?.popupStrDtm));
      })
      .catch((error) => {
        console.error("Error loading content data:", error);
      });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleFileChange = (e, slotIndex) => {
    const file = e.target.files[0];
    setFiles((prev) => prev.map((item, index) => (index === slotIndex ? file : item)));
  };

  const handleDeleteFileChange = (e, slotIndex) => {
    const checked = e.target.checked;
    setDeleteFiles((prev) =>
      prev.map((item, index) => (index === slotIndex ? checked : item))
    );
  };

  const showDialog = (messageCode) => {
    setDialogMessage(messageCode);
    setDialogOpen(true);
  };

  const validate = () => {
    const newErrors = {
      title: !title.trim(),
      content: !content.trim(),
      popupPeriod: false,
    };

    if (popupYn && (!popupTarget || !popupStrDtm || !popupEndDtm)) {
      newErrors.popupPeriod = true;
    }

    setErrors(newErrors);

    if (newErrors.title || newErrors.content || newErrors.popupPeriod) {
      return false;
    }

    if (popupYn) {
      if (!moment(popupEndDtm).isAfter(moment(popupStrDtm))) {
        showDialog("L1139");
        return false;
      }
      // 시작일시를 변경한 경우에만 당일 이후 여부를 검증한다.
      if (popupStrDtm !== originStrDtm && moment(popupStrDtm).isBefore(todayStart)) {
        showDialog("L1141");
        return false;
      }
    }

    return true;
  };

  const buildFormData = () => {
    const userId = JSON.parse(sessionStorage.getItem("userInfo"))?.userId || "";
    const notice = {
      title,
      content,
      popupYn: popupYn ? "Y" : "N",
      popupTarget: popupYn ? popupTarget : null,
      // 팝업 기간은 UTC로 저장한다. (입력값은 브라우저 로컬 시각)
      popupStrDtm: popupYn ? toUtcDateTime(popupStrDtm) : null,
      popupEndDtm: popupYn ? toUtcDateTime(popupEndDtm) : null,
    };

    if (isViewMode) {
      notice.id = Number(id);
      notice.updrId = userId;
      notice.delFile1 = deleteFiles[0];
      notice.delFile2 = deleteFiles[1];
      notice.delFile3 = deleteFiles[2];
    } else {
      notice.crerId = userId;
    }

    const formData = new FormData();
    formData.append(
      "notice",
      new Blob([JSON.stringify(notice)], { type: "application/json" })
    );
    files.forEach((file, index) => {
      if (file) {
        formData.append(`file${index + 1}`, file);
      }
    });

    return formData;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const url = isViewMode ? "/notice/main/update" : "/notice/main/save";

    api
      .post(url, buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        const resultCode = response?.data;

        if (resultCode === 0) {
          // 수정 저장은 완료 안내 없이 바로 목록으로 이동한다.
          if (isViewMode) {
            navigate("/notice/main");
            return;
          }
          showDialog("L0103");
          return;
        }

        const messageCode = RESULT_MESSAGE_CODES[resultCode];
        if (messageCode) {
          showDialog(messageCode);
          return;
        }

        // 정의되지 않은 결과 코드도 반드시 알린다. (무반응으로 끝나지 않도록)
        console.error("Unexpected notice save result code:", resultCode);
        showDialog("L1142");
      })
      .catch((error) => {
        console.error("Error saving notice:", error);
        showDialog("L1142");
      });
  };

  const handleDownloadFile = async (noticeId, fileName) => {
    if (!fileName) return;

    try {
      const response = await api.post(
        "/notice/main/download",
        qs.stringify({ id: noticeId, fileName }),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          responseType: "blob",
        }
      );
      saveBlobAsFile(response.data, fileName);
    } catch (error) {
      console.log("Error downloading file:", error);
      showDialog("L0957");
    }
  };

  const storedFileNames = [data?.fileName1, data?.fileName2, data?.fileName3];
  const hasStoredFile = storedFileNames.some((fileName) => fileName);

  return (
    <>
      <BreadCrumbDiv items={[t("MENU:L0055"), t("MENU:L0069"), t("L0134")]} />
      <LoadingBoxDiv>
        <InputWrapDiv>
          <TopBoxDiv value={t("L0134")} />
          <table>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "auto" }} />
            </colgroup>
            <tbody>
              <tr>
                <th>{t("L0131")}</th>
                <td>
                  <TooltipWrapper show={errors.title} message={t("L0658")}>
                    <ContentInput
                      value={title}
                      disabled={!isEditing}
                      width="100%"
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </TooltipWrapper>
                </td>
              </tr>
              <tr>
                <th style={{ verticalAlign: "top" }}>{t("L0136")}</th>
                <td>
                  <TooltipWrapper show={errors.content} message={t("L0658")}>
                    <ContentTextarea
                      value={content}
                      disabled={!isEditing}
                      width="100%"
                      height="500px"
                      onChange={(e) => setContent(e.target.value)}
                    />
                  </TooltipWrapper>
                </td>
              </tr>

              {/*
                팝업 공지 설정은 등록·수정 화면에서만 노출한다(조회 화면에서는 감춤).
                전역 CSS가 native 입력을 숨기므로 인접 label을 체크박스 이미지로 사용한다.
              */}
              {isEditing && (
                <tr>
                  <th>{t("L1129", "Popup Notice")}</th>
                  <td>
                    <input
                      type="checkbox"
                      id="popup_yn"
                      checked={popupYn}
                      onChange={(e) => setPopupYn(e.target.checked)}
                    />
                    <label htmlFor="popup_yn" style={CHECK_LABEL_STYLE}>
                      {t("L1129", "Popup Notice")}
                    </label>
                  </td>
                </tr>
              )}
              {isEditing && popupYn && (
                <>
                  <tr>
                    <th>{t("L1130", "Popup Screen")}</th>
                    <td>
                      <input
                        type="radio"
                        id="popup_target_login"
                        name="popup_target"
                        value={POPUP_TARGET.LOGIN}
                        checked={popupTarget === POPUP_TARGET.LOGIN}
                        onChange={(e) => setPopupTarget(e.target.value)}
                      />
                      <label
                        htmlFor="popup_target_login"
                        style={{ ...CHECK_LABEL_STYLE, marginRight: "20px" }}
                      >
                        {t("L1131", "Login Screen")}
                      </label>
                      <input
                        type="radio"
                        id="popup_target_main"
                        name="popup_target"
                        value={POPUP_TARGET.MAIN}
                        checked={popupTarget === POPUP_TARGET.MAIN}
                        onChange={(e) => setPopupTarget(e.target.value)}
                      />
                      <label htmlFor="popup_target_main" style={CHECK_LABEL_STYLE}>
                        {t("L1132", "Main Screen")}
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <th>{t("L1133", "Popup Period")}</th>
                    <td>
                      <TooltipWrapper
                        show={errors.popupPeriod}
                        message={t("L0658")}
                      >
                        <ContentInput
                          type="datetime-local"
                          value={popupStrDtm}
                          width="220px"
                          min={strDtmMin}
                          onChange={(e) => setPopupStrDtm(e.target.value)}
                        />
                        <span style={{ margin: "0 6px" }}>~</span>
                        <ContentInput
                          type="datetime-local"
                          value={popupEndDtm}
                          width="220px"
                          min={popupStrDtm || todayStart}
                          onChange={(e) => setPopupEndDtm(e.target.value)}
                        />
                      </TooltipWrapper>
                    </td>
                  </tr>
                </>
              )}

              {/* 첨부파일 - 슬롯 단위로 유지 / 삭제 / 교체를 처리한다. */}
              {(isEditing || hasStoredFile) && (
                <tr>
                  <th style={{ verticalAlign: "top" }}>
                    {t("L0939")} (PDF, WORD, EXCEL, PPT)
                  </th>
                  <td>
                    {FILE_SLOTS.map((slotIndex) => {
                      const storedFileName = storedFileNames[slotIndex];
                      const isDeleted = deleteFiles[slotIndex];
                      // 기존 파일이 남아 있고 삭제 요청도 없으면 파일명과 다운로드를 보여준다.
                      const showStoredFile = storedFileName && !isDeleted;

                      if (showStoredFile) {
                        return (
                          <div key={slotIndex} style={{ marginBottom: "4px" }}>
                            <ContentInput
                              type="text"
                              value={storedFileName}
                              width={isEditing ? "70%" : "90%"}
                              style={{ marginRight: "0px" }}
                              readOnly
                            />
                            <FunctionButton
                              className="small"
                              value={t("L0322")}
                              onClick={() =>
                                handleDownloadFile(data.id, storedFileName)
                              }
                              style={{ width: "10%", margin: "0px" }}
                            />
                            {isEditing && (
                              <>
                                <input
                                  type="checkbox"
                                  id={`del_file_${slotIndex}`}
                                  checked={isDeleted}
                                  onChange={(e) =>
                                    handleDeleteFileChange(e, slotIndex)
                                  }
                                />
                                <label
                                  htmlFor={`del_file_${slotIndex}`}
                                  style={{
                                    ...CHECK_LABEL_STYLE,
                                    marginLeft: "8px",
                                  }}
                                >
                                  {t("L0091")}
                                </label>
                              </>
                            )}
                          </div>
                        );
                      }

                      if (!isEditing) {
                        return null;
                      }

                      return (
                        <div key={slotIndex} style={{ marginBottom: "4px" }}>
                          <ContentInput
                            type="file"
                            onChange={(e) => handleFileChange(e, slotIndex)}
                          />
                          {storedFileName && isDeleted && (
                            <>
                              <input
                                type="checkbox"
                                id={`del_file_${slotIndex}`}
                                checked={isDeleted}
                                onChange={(e) =>
                                  handleDeleteFileChange(e, slotIndex)
                                }
                              />
                              <label
                                htmlFor={`del_file_${slotIndex}`}
                                style={{
                                  ...CHECK_LABEL_STYLE,
                                  marginLeft: "8px",
                                }}
                              >
                                {t("L0091")} : {storedFileName}
                              </label>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </InputWrapDiv>
      </LoadingBoxDiv>
      <ButtonWrapDiv className="btn_bottom">
        <FunctionButton
          value={t("L0005")}
          className="bg_gray big"
          onClick={handleCancel}
        />
        {isViewMode && !isEditing && menuAuthInfo.edit && (
          <FunctionButton
            value={t("L1137", "Modify")}
            className="big"
            onClick={() => setIsEditing(true)}
          />
        )}
        {isEditing && (
          <FunctionButton
            value={t("L0098") + " " + t("L0466")}
            className="big"
            onClick={handleSubmit}
          />
        )}
      </ButtonWrapDiv>

      {dialogOpen && (
        <MessageDialog
          title={t("L0339")}
          message={t(dialogMessage, DIALOG_DEFAULT_MESSAGES[dialogMessage])}
        >
          {dialogMessage === "L0103" ? (
            <FunctionButton
              value={t("L0004")}
              onClick={() => {
                setDialogOpen(false);
                navigate("/notice/main");
              }}
            />
          ) : (
            <FunctionButton
              value={t("L0004")}
              onClick={() => {
                setDialogOpen(false);
              }}
            />
          )}
        </MessageDialog>
      )}
    </>
  );
};

export default MainNoticeViewPage;
